import { PortableText } from '@portabletext/react'
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next'
import { useLiveQuery } from '~/lib/sanity-live-query'
import { getGlobalData } from '../../utils/global-data';
import Footer from '../../components/Footer';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import StickyNavbar from '../../components/StickyNavbar';
import HorizontalLine from '../../components/HorizontalLine';
import CarouselTitle from "~/components/CarouselTitle/CarouselTitle";
import SliderCategory from '../../components/Sliders/SliderCategory';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'

import { readToken } from '~/lib/sanity.api'
import { getClient } from '~/lib/sanity.client'
import {
  getTimber,
  getTimbers,
  getSiteConfig,
  timberBySlugQuery,
  timbersQuery,
  siteConfigQuery,
  getSystems,
  systemsQuery,
} from '~/lib/sanity.queries'
import { type Timber, SiteConfig, System } from '~/lib/sanity.interfaces'
import type { SharedPageProps } from '~/pages/_app'
import Link from 'next/link';

  interface Query {
    [key: string]: string
  }
  
  export const getServerSideProps: GetServerSideProps<
    SharedPageProps & {
      timber: Timber,
      timbers: Timber[],
      systems: System[],
      siteConfig: SiteConfig,
    },
    Query
  > = async ({ draftMode = false, params = {} }) => {
    const client = getClient(draftMode ? { token: readToken } : undefined)

    const timber = await getTimber(client, params.slug)
    const timbers = await getTimbers(client)
    const systems = await getSystems(client)
    const siteConfig = await getSiteConfig(client)
    const globalData = getGlobalData()
  
    if (!timber) {
      return {
        notFound: true,
      }
    }
  
    return {
      props: {
        globalData,
        draftMode,
        token: draftMode ? readToken : '',
        timber,
        timbers,
        systems,
        siteConfig,
      },
    }
  }

export default function TimberPage(
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
  globalData
) {
  const [timber] = useLiveQuery(props.draftMode, props.timber, timberBySlugQuery, {
    slug: props.timber.slug.current,
  })
  const [siteConfig] = useLiveQuery(props.draftMode, props.siteConfig, siteConfigQuery)
  const [timbers] = useLiveQuery<Timber[]>(props.draftMode, props.timbers, timbersQuery)
  const [systems] = useLiveQuery<System[]>(props.draftMode, props.systems, systemsQuery)

  const slides = []
  if(timber.mainCarousel) {
    timber.mainCarousel.map((item) => {
      slides.push({
        _id: item._id,
        image: item,
        title: item.title
      });
    })
  }

  return (
    <Layout>
        <SEO title={timber.seoTitle} description={timber.seoDescription} keywords={timber.seoKeywords} />
        <StickyNavbar siteConfig={siteConfig} />
        <main className="w-full bg-background nav-offset py-4 lg:py-8">  
            <div className='mx-2 md:mx-10'>
                <div className='grid grid-cols-1 lg:grid-cols-2'>
                  <div className='border-r lg:border-r-0 border-t border-l border-b border-black '>
                    <div className='p-10 border-b border-black'>
                      <h1 className="text-3xl lg:text-5xl text-left mb-8 mr-10">
                        {timber.title}
                      </h1> 
                      <div className='mb-10'>
                        <h2 className='text-lg lg:text-lg font-bold'>Species Name</h2>
                        <h3 className='text-lg lg:text-lg'>{timber.species}</h3>
                      </div>
                      <div className='mb-10'>
                        <h2 className='text-lg lg:text-lg font-bold'>Other Names</h2>
                        <h3 className='text-lg lg:text-lg'>{timber.otherName}</h3>
                      </div>
                      <div className='mb-10'>
                        <h2 className='text-lg lg:text-lg font-bold'>Country of Origin</h2>
                        <h3 className='text-lg lg:text-lg'>{timber.countryOfOrigin}</h3>
                      </div>
                      <div className=''>
                        <h2 className='text-lg lg:text-lg font-bold'>Mainly Used For</h2>
                        <h3 className='text-lg lg:text-lg'>{timber.uses}</h3>
                      </div>
                    </div>
                    <div className='p-10 contentBlock text-left text-lg'>
                      <PortableText value={timber.body} />
                    </div>
                  </div>
                  <div className=''>
                    <CarouselTitle slides={slides} />
                  </div>
                </div>
                <div className='flex flex-col gap-2 lg:flex-row justify-between mt-12'>
                  {timber.factSheetURL ? (
                    <a href={timber.factSheetURL} target='_blank' className="flex items-center gap-3 p-4 px-8 bg-darkButton hover:bg-white min-w-80 border-2 border-black">
                      Fact Sheet
                      <ArrowDownTrayIcon className='w-6' />
                    </a>
                  ): (
                    <>
                    </>
                  )}  
                  {timber.brochureSheetURL ? (
                    <a href={timber.brochureSheetURL} target='_blank' className="flex items-center gap-3 p-4 px-8 bg-darkButton hover:bg-white min-w-80 border-2 border-black">
                      Brochure
                      <ArrowDownTrayIcon className='w-6' />
                    </a>
                  ): (
                    <>
                    </>
                  )} 
                  {timber.specSheetURL ? (
                    <a href={timber.specSheetURL} target='_blank' className="flex items-center gap-3 p-4 px-8 bg-darkButton hover:bg-white min-w-80 border-2 border-black">
                      Spec Sheet
                      <ArrowDownTrayIcon className='w-6' />
                    </a>
                  ): (
                    <>
                    </>
                  )}      
                </div>
                <HorizontalLine />
            </div>  
            <div className='mx-2 md:mx-10'>
               {timber.selectedTimbers && (
                <>
                  <div className="text-right">
                      <h3 className="text-3xl font-semibold text-primary mb-10">Other Timbers</h3>
                  </div> 
                  <SliderCategory items={timber.selectedTimbers} prefix="/timber/" />
                  <HorizontalLine />
                </>
               )}                
               {timber.selectedSystems && (
                <>
                  <div className="text-right">
                      <h3 className="text-4xl font-semibold text-primary mb-10">Available Timber Systems</h3>
                  </div> 
                  <SliderCategory items={timber.selectedSystems} prefix="/systems/" />
                  <div className='flex justify-end'>
                    <Link className="mt-2 font-bold underline" href="/timber">More</Link>
                  </div>
                </>
               )}                
            </div>  
            </main>        
        <Footer siteConfig={siteConfig} />      
    </Layout>
  );
}